/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Icon, Typography } from "./..";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useImperativeHandle, useMemo, useRef } from 'react';
import { itemsCenter, bgTransparent, boxBorder, flexRow, parseMinHeight, parseWidth } from "../../styles/general";
import { useTheme, useColor as colors } from "../../theme";
import { classNames } from "../../utils";
const {
  colors: {
    system: {
      rest: systemRest
    },
    fill: {
      sidebar: fillSidebar
    }
  },
  spacing
} = useTheme();
const colorMap = new Map([['default', systemRest]]);
const iconSizeMap = new Map([['small', '16px'], ['medium', '20px'], ['large', '24px']]);
const typographySizeMap = new Map([['small', 'p3'], ['medium', 'p2'], ['large', 'p1']]);
const filledPaddingSizeMap = new Map([['small', spacing([0.5, 2])], ['medium', spacing([0.5, 2])], ['large', spacing([0.5, 3])]]);
const Status = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  className,
  color: colorProp,
  icon,
  id,
  size,
  style,
  text,
  viewType
}, reference) => {
  if (!reference) reference = useRef(null);
  const ref = useRef(null);
  const outlinedPaddingSize = filledPaddingSizeMap.get(size);
  const color = colorMap.get(colorProp) || colors[colorProp] || colorProp || systemRest;
  const iconSize = iconSizeMap.get(size);
  const typographySize = typographySizeMap.get(size);
  const _StatusRootCSS = StatusRootCSS(iconSize, color, size, outlinedPaddingSize);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const StatusIconView = typeof icon === 'string' ? jsx(Icon, {
    name: icon,
    width: iconSize,
    height: iconSize,
    color: 'currentColor',
    viewBox: true
  }) : icon;
  const StatusTextView = jsx(Typography, {
    css: TextCSS,
    type: typographySize,
    color: 'inherit'
  }, text);
  return useMemo(() => {
    return jsx("div", {
      css: _StatusRootCSS,
      ref: ref,
      id: id,
      style: style,
      className: classNames('DGN-UI-Status', viewType, size, className)
    }, StatusIconView, StatusTextView);
  }, [className, colorProp, icon, id, size, style, text, viewType]);
}));
const StatusRootCSS = (iconSize, color, size, outlinedPaddingSize) => css`
    ${flexRow};
    ${itemsCenter};
    ${bgTransparent};
    ${boxBorder};
    ${parseWidth('fit-content')};
    ${parseMinHeight(iconSize)};
    gap: ${spacing([size === 'small' ? 0.5 : 1])};
    color: ${color};
    &.filled {
        color: ${fillSidebar};
        background-color: ${color};
        border-radius: 20px;
        &.${size} {
            padding: ${outlinedPaddingSize};
        }
    }
`;
const TextCSS = css`
    white-space: nowrap;
`;
Status.defaultProps = {
  className: '',
  icon: '',
  size: 'medium',
  style: {},
  text: '',
  viewType: 'ghost'
};
Status.propTypes = {
  /** Class for component. */
  className: PropTypes.string,
  /** The color of the component. */
  color: PropTypes.string,
  /** [Icon](https://core.diginet.com.vn/ui/?path=/story/icon-basic--basic) or element placed before the content. */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** The size of the component. */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Style inline of component. */
  style: PropTypes.object,
  /** The content of the component. */
  text: PropTypes.string,
  /** The variant to use. */
  viewType: PropTypes.oneOf(['filled', 'ghost'])
};
export { Status };
export default OptionWrapper(Status);